/**
 * Share Document Screen page component module.
 * @module investor/shared/pages/ShareDocument
 */

import React, {useEffect, useState} from 'react';
import history from '../../../history';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useParams} from 'react-router-dom';
import useSWR from 'swr';
import lazy from 'lazy.js';
import debug from 'debug';

import {
  Button,
  DescriptiveText,
  LoadingSpinner,
  Page,
  Row,
  Table,
  TextInput,
  Tile,
} from '../../../shared';
import {_openModal} from '../../../shared/reducers/modals';
import {_setNotification} from '../../../shared/reducers/headerNotification';
import {gatherData, getUpdatedFilter, setSectionURI} from '../../../shared/utils/utils';
import axiosInstance from '../../../shared/utils/axios';

const log = debug('circleblack:share-document');

const ShareDocument = props => {
  const {profile, setNotification, openModal} = props;
  const {id} = useParams();
  const [document, setDocument] = useState({});
  const [selectedAdvisor, setSelectedAdvisor] = useState([]);
  const [processing, setProcessing] = useState(false);
  const getDocument = gatherData(`/documents/${id}`);
  const getSharedAdvisors = gatherData(`/documents/${id}/advisors`);
  const defaultParams = {};
  const {data: advisors} = useSWR('/user/advisors');
  const [shareableAdvisor, setShareableAdvisor] = useState(advisors);

  const submitShare = () => {
    const shareBody = {};
    setProcessing(true);

    shareBody.households = lazy(selectedAdvisor).pluck('id').toArray();
    return axiosInstance.post(`/documents/${document.id}/library`, shareBody)
      .then(() => {
        setProcessing(false);
        history.push(setSectionURI(`/${document.id}/sharing`));
        setNotification({
          message: `${document.title} has been shared with the selected ${profile.lowerCasePluralAdvisorTerm}.`,
        });
      })
      .catch(err => {
        setProcessing(false);
        openModal({
          type: 'Error',
          title: 'Failed to share document',
          err: err,
        });
      });
  };

  const sharedAdvisorsDataManager = {
    setSelectedRows: rData => {
      setSelectedAdvisor(rData);
    },
  };

  useEffect(() => {
    if (!document.id) {
      getDocument()
        .then(doc => {
          if (doc.notShareable || doc.cat === 'Report' && !doc.assocHouseholdID) {
            history.push(setSectionURI());
            return;
          }
          setDocument(doc);
        })
        .catch(function error(err) {
          log('error was caught in useEffect', err);
          if (!(err.response.status < 500 && err.response?.data?.error?.msg)) {
            err.message = 'Unfortunately we encountered an error sharing the document. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.';
          }
          throw err;
        });
    }
  }, []);

  useEffect(() => {
    if (document.cat === 'Report' && document.assocHouseholdID) {
      defaultParams.id = document.assocHouseholdID;
    }
    getSharedAdvisors({
      ...getUpdatedFilter({...defaultParams}),
    })
      .then(sAdvisors => setShareableAdvisor(advisors.filter(a => !lazy(sAdvisors).pluck('id').toArray().includes(a.id))))
      .catch(err => {
        log('err thrown');
        history.push(setSectionURI());
        throw err;
      });
  }, [document, advisors]);

  return <Page>
    <Tile key={'ShareDocument'} title={'Share Document'}>
      <Row justifySubRow={'space between'}>
        <TextInput type={'text'}
                   title={'Document Name'}
                   placeholder={'Document Name'}
                   dataPoint={{name: 'title', value: document.title}}
                   onChange={() => {}}
                   width={'fit-content'}
                   readOnly={true}/>
        <Row alignItems={'right'} width={'fit-content'}>
          <Button width={'150px'}
                  importance={'secondary'}
                  size={'md'}
                  label={'Cancel'}
                  action={() => history.push(setSectionURI())}/>
          <Button width={'150px'}
                  importance={'primary'}
                  size={'md'}
                  label={'Share'}
                  action={submitShare}
                  processing={processing}
                  disabled={!selectedAdvisor.length}/>
        </Row>
      </Row>
      <Row>
        <DescriptiveText>
          Select the individual {profile.lowerCasePluralAdvisorTerm} you want to receive this {document.type === 'Article'
            ? 'article'
            : 'document'}, or select all.
        </DescriptiveText>
      </Row>
      {!!document.id
        ? <Table dataSet={shareableAdvisor}
                 dataManager={sharedAdvisorsDataManager}
                 columns={[
                   {
                     title: 'Advisor',
                     dataPoint: 'name',
                     type: 'text',
                     minWidth: 120,
                     isSortable: true,
                   },
                   {
                     title: 'Email',
                     dataPoint: 'email',
                     type: 'text',
                     minWidth: 120,
                     isSortable: true,
                   },
                 ]}
                 defaultSort={{
                   column: 'name',
                   desc: false,
                 }}
                 select={{type: 'page', max: 100, preselectedRows: selectedAdvisor}}
                 rowIdentifier={'id'}
                 height={'500px'}/>
        : <LoadingSpinner/>}
    </Tile>
  </Page>;
};

const mapStateToProps = state => ({
  profile: state.session.profile,
});
const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  setNotification: note => dispatch(_setNotification(note)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShareDocument);

ShareDocument.propTypes = {
  /**
   * The open function that dispatches _openModal
   */
  openModal: PropTypes.func.isRequired,
  profile: PropTypes.object.isRequired,
  /**
   * Notification for share confirmation in header. Takes object with message property.
   */
  setNotification: PropTypes.func.isRequired,
};
